/**
 * Meteor Strike — Plan 4a Task 8.1
 *
 * Targets a single player (no skillshot, no range limit).
 * - On hit: 20 damage via applyDamage.
 * - Stone Skin block: consumed, item_blocked_stoneskin emitted, no damage.
 * - Missing targetPlayerId: throws Error.
 *
 * Self-registers via addItem(meteorStrike) at module load.
 */
import type { GameState } from '../types';
import type { ItemCastCtx, ItemCastResult, Item } from './types';
import { appendEvent } from '../event-log';
import { applyDamage } from '../damage';
import { addItem } from './registry';
import { tryBlockWithStoneSkin } from '../stone-skin-block';

export const meteorStrike: Item = {
  id: 'meteor_strike',
  name: 'Meteor Strike',
  description:
    'Calls down a meteor strike on a target player, dealing 20 damage. Blocked by Stone Skin.',
  targetType: 'player',
  category: 'simple',
  rarity: 'uncommon',
  blockedByStoneSkin: true,

  cast(state: GameState, ctx: ItemCastCtx): ItemCastResult {
    const { casterId } = ctx;
    const targetId = ctx.target.targetPlayerId;

    // Step 1: Validate caster and target
    if (!targetId) throw new Error('Meteor Strike: missing targetPlayerId in ctx.target');

    const caster = state.players.get(casterId);
    if (!caster) throw new Error(`Meteor Strike: caster not found: ${casterId}`);

    const target = state.players.get(targetId);
    if (!target) throw new Error(`Meteor Strike: target not found: ${targetId}`);

    // Step 2: Emit item_cast
    appendEvent(state, {
      type: 'item_cast',
      playerId: casterId,
      casterId,
      itemId: 'meteor_strike',
      targetId,
    });

    if (tryBlockWithStoneSkin(state, targetId, { actorId: casterId, sourceId: 'meteor_strike' })) {
      return { success: true, blockedByStoneSkin: true };
    }

    // Step 3: Hit lands — emit item_hit, then apply 20 damage
    appendEvent(state, {
      type: 'item_hit',
      playerId: casterId,
      casterId,
      itemId: 'meteor_strike',
      targetId,
      damageDealt: 20,
    });

    applyDamage(state, targetId, 20, 'item_meteor_strike', casterId);
    return { success: true, damageDealt: 20 };
  },
};

// Self-register at module load
addItem(meteorStrike);
